import { ROOT_ID } from '@flobi/artwork-core';
import { styleFill, styleStroke } from '../colors';
import type { IconName } from '../chrome/icons';
import type { ToolId } from '../useEditorCanvas';
import type { Point, Rail, RailButton, Tool, ToolContext } from './types';

/**
 * The shape group: rectangle, ellipse, polygon, star and line.
 *
 * One tool, five kinds, because everything but the line is the same gesture — press,
 * drag out a box, release — and the kind only decides what is written into the node.
 * The engine has had polygon, star and line nodes since the first week and nothing in
 * the editor ever made one; this is that gap closed.
 *
 * The shape is created on the press rather than the release, so other people watch it
 * being drawn instead of seeing it appear finished — the same reason the pen writes its
 * path as it goes.
 */
export type ShapeKind = 'rect' | 'ellipse' | 'polygon' | 'star' | 'line';

/** What each kind is called when it lands in the layers list. */
const NAMES: Record<ShapeKind, string> = {
  rect: 'Rectangle',
  ellipse: 'Ellipse',
  polygon: 'Polygon',
  star: 'Star',
  line: 'Line',
};

/**
 * The family, in the order the bar has always listed them.
 *
 * Shared with the arc, which is built out of a path rather than a shape node — a
 * difference that belongs to the code and not to the rail, so the list is one list.
 */
export const KINDS: { id: ToolId; icon: IconName; title: string }[] = [
  { id: 'rect', icon: 'square', title: 'Rectangle' },
  { id: 'ellipse', icon: 'circle', title: 'Ellipse' },
  { id: 'polygon', icon: 'polygon', title: 'Polygon' },
  { id: 'star', icon: 'star', title: 'Star' },
  { id: 'line', icon: 'line', title: 'Line' },
  { id: 'arc', icon: 'arc', title: 'Arc' },
];

export function shapeTool(kind: ShapeKind): Tool {
  /**
   * `from` is where the first corner LANDED and `pressed` is where the hand actually
   * was. They are not the same once the corner snaps, and the difference matters: the
   * shape is measured from where it landed, while whether the press was a click or a
   * drag is a fact about the hand. Measuring the travel from the snapped corner made a
   * plain click on the line tool read as a drag whenever the corner had moved.
   */
  let drawing: { id: string; from: Point; pressed: Point; origin: Point } | null = null;
  /**
   * A line whose first end was CLICKED rather than dragged, waiting for its second press.
   *
   * Two ways to draw the same line, because the hand has two habits: drag it out in one
   * movement, or set one end down, look at where it reaches, and set the other. The second
   * is the one you want when the far end is somewhere precise, or off the far side of the
   * screen — a drag has to be held the whole way and cannot be reconsidered.
   */
  let placing = false;
  /** How far a press has to travel before it counts as a drag rather than a click. */
  const DRAG_SLOP = 3;
  /**
   * What a plain click makes, in document units: a shape of this size centred on it, as
   * the Artboard tool places a preset. A click used to leave a 1×1 speck in the layers
   * and hand back to Select (QA 2026-09-24, shapes-F9).
   */
  const CLICK_SIZE = 100;
  /**
   * The smallest a shape being dragged out can be: one screen pixel, never more than one
   * unit. A floor of one UNIT made a small drag at high zoom jump to a box 256 pixels
   * wide, so fine detail could not be drawn (QA 2026-09-24, shapes-F16).
   */
  const least = (ctx: ToolContext) => Math.min(1, ctx.unitsPerPixel);

  /** The shape being drawn goes, and the tool stays in hand for the next one. */
  const cancel = (ctx: ToolContext) => {
    if (drawing && ctx.node(drawing.id)) ctx.edit([{ op: 'delete', id: drawing.id }]);
    placing = false;
    drawing = null;
    ctx.setSelection([]);
    ctx.stopCapturing();
    ctx.refresh();
  };

  /**
   * The tool's own settings, which are not the document's: how many sides the next
   * polygon has, how deep the next star's points go. They live here because they belong
   * to the tool rather than to any shape, and they are what the rail changes.
   */
  const settings = { sides: 6, points: 5, innerRatio: 0.382, radius: 0 };

  const style = (ctx: ToolContext) => {
    const chosen = ctx.style;
    // A line has no inside, so its colour is its stroke's; everything else fills unless
    // the fill has been switched off, and outlines in whatever colour the stroke is.
    const filled = kind !== 'line' && chosen.fillOn;
    return {
      opacity: chosen.opacity,
      fills: filled ? [{ type: 'solid', color: styleFill(chosen) }] : [],
      ...(chosen.strokeWeight > 0 || kind === 'line'
        ? {
            strokes: [{ type: 'solid', color: styleStroke(chosen) }],
            strokeStyle: { weight: Math.max(kind === 'line' ? 1 : 0, chosen.strokeWeight) },
          }
        : {}),
    };
  };

  /** What the node is born as, before the drag gives it a size. */
  const shapeOf = (side: number) => {
    if (kind === 'line') return { type: 'line', length: side };
    const size = { width: side, height: side };
    if (kind === 'polygon') return { type: 'polygon', ...size, sides: Math.round(settings.sides), cornerRadius: settings.radius };
    if (kind === 'star') return { type: 'star', ...size, points: Math.round(settings.points), innerRatio: settings.innerRatio, cornerRadius: settings.radius };
    if (kind === 'rect') return { type: 'rect', ...size, radii: cornersFor(side, side) };
    return { type: 'ellipse', ...size };
  };

  /**
   * The strip's corner radius, held to half the shorter side of the box it is going on.
   * The strip runs to 200 whatever the size, and a small rectangle drawn with it stored a
   * radius past the end of the inspector's slider — the corners look the same (they are
   * scaled down when drawn) but the number read wrong and touching the slider snapped it
   * (QA 2026-09-24, shapes-F17).
   */
  const cornersFor = (width: number, height: number) => {
    const r = Math.min(settings.radius, Math.min(width, height) / 2);
    return [r, r, r, r];
  };

  /** Where the hand last put a line's far end, so Shift going down or up redraws it there. */
  let lastLineAt: Point | null = null;

  /**
   * A line has a length and an angle rather than a box: the transform carries the angle,
   * so the node stays one straight thing rather than a path of two points.
   *
   * Shift holds it to eighths of a turn — level, upright or diagonal — and the far end is
   * the hand carried onto that direction, so it stays under the pointer along the line
   * rather than overshooting by the part of the move that was thrown away.
   */
  const stretchLine = (ctx: ToolContext, world: Point, straight: boolean) => {
    if (!drawing) return;
    const dx = world.x - drawing.from.x;
    const dy = world.y - drawing.from.y;
    let angle = Math.atan2(dy, dx);
    let reach = Math.hypot(dx, dy);
    if (straight) {
      angle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
      reach = dx * Math.cos(angle) + dy * Math.sin(angle);
    }
    const length = Math.max(least(ctx), reach);
    // Upright and level exactly: cos(π/2) is 6e-17, not nought.
    const cos = Math.abs(Math.cos(angle)) < 1e-12 ? 0 : Math.cos(angle);
    const sin = Math.abs(Math.sin(angle)) < 1e-12 ? 0 : Math.sin(angle);
    ctx.edit([
      {
        op: 'set',
        id: drawing.id,
        props: {
          length,
          transform: [cos, sin, -sin, cos, drawing.origin.x, drawing.origin.y],
        },
      },
    ]);
  };

  return {
    id: kind,

    onPointerDown(event, rawWorld, ctx) {
      // Where a shape STARTS is as much a decision as where it ends, so the first corner
      // lands on a guide or an edge exactly as the second one does.
      const world = ctx.place(rawWorld);
      // A line being placed whose node has gone — deleted from the Layers list, or by a
      // collaborator — is no longer being placed: this press starts a new shape instead of
      // finishing one that is not there (QA 2026-09-24, shapes-F14).
      if (placing && drawing && !ctx.node(drawing.id)) {
        placing = false;
        drawing = null;
      }
      // The second press of a click-click line: it already reaches here, so this ends it.
      if (placing && drawing) {
        // Pressed again on the first end — a double-click — is a line of no length, not a
        // line: it goes, and the tool stays in hand (QA 2026-09-24, shapes-F9).
        if (Math.hypot(rawWorld.x - drawing.pressed.x, rawWorld.y - drawing.pressed.y) < DRAG_SLOP * ctx.unitsPerPixel) {
          cancel(ctx);
          return true;
        }
        placing = false;
        drawing = null;
        ctx.stopCapturing();
        ctx.finish();
        return true;
      }

      const parent = ctx.artboardAt(world);
      const parentOrigin = parent ? ctx.scene.worldTransform(parent) : [1, 0, 0, 1, 0, 0];
      const origin = { x: world.x - parentOrigin[4]!, y: world.y - parentOrigin[5]! };

      const result = ctx.edit([
        {
          op: 'create',
          ref: 'shape',
          parentId: parent ?? ROOT_ID,
          node: {
            ...shapeOf(least(ctx)),
            name: NAMES[kind],
            transform: [1, 0, 0, 1, origin.x, origin.y],
            ...style(ctx),
          },
        },
      ]);
      if (!result.ok) return false;
      drawing = { id: result.created.shape!, from: world, pressed: rawWorld, origin };
      lastLineAt = null;
      ctx.setSelection([drawing.id]);
      return true;
    },

    onPointerMove(event, rawWorld, ctx) {
      // A line being placed follows the pointer with nothing pressed — that ghost IS the
      // feedback that the first end is down and the second is still in the hand.
      if (!drawing || (event.buttons === 0 && !placing)) return;
      // Never against ITSELF: the shape being drawn is on the page from the first press,
      // so without this its own growing edge is the nearest thing to line up with.
      const world = ctx.place(rawWorld, [drawing.id]);
      const node = ctx.node(drawing.id);
      if (!node) return;

      if (kind === 'line') {
        lastLineAt = world;
        stretchLine(ctx, world, event.shiftKey);
        return;
      }

      // The corner the drag started at stays put and the shape grows towards the
      // pointer, so a shape can be drawn in any direction.
      const width = Math.abs(world.x - drawing.from.x);
      const height = Math.abs(world.y - drawing.from.y);
      const square = event.shiftKey ? Math.max(width, height) : 0;
      // With Shift the box is square, so its far corner is NOT the pointer: dragged up or
      // left, the corner is the start minus the side, or the square lands off the start
      // (QA 2026-09-24, shapes-F1).
      const left = square ? (world.x < drawing.from.x ? drawing.from.x - square : drawing.from.x) : Math.min(world.x, drawing.from.x);
      const top = square ? (world.y < drawing.from.y ? drawing.from.y - square : drawing.from.y) : Math.min(world.y, drawing.from.y);
      const x = left - (drawing.from.x - drawing.origin.x);
      const y = top - (drawing.from.y - drawing.origin.y);
      const t = node.transform;
      const w = Math.max(least(ctx), square || width);
      const h = Math.max(least(ctx), square || height);
      ctx.edit([
        {
          op: 'set',
          id: drawing.id,
          props: {
            width: w,
            height: h,
            ...(kind === 'rect' ? { radii: cornersFor(w, h) } : {}),
            transform: [t[0], t[1], t[2], t[3], x, y],
          },
        },
      ]);
    },

    onPointerUp(_event, world, ctx) {
      if (!drawing) return;
      // A press that never travelled is a click, and a clicked line is only half drawn:
      // it stays, following the pointer, until the press that says where it ends.
      const still = Math.hypot(world.x - drawing.pressed.x, world.y - drawing.pressed.y) < DRAG_SLOP * ctx.unitsPerPixel;
      if (kind === 'line' && still) {
        placing = true;
        return;
      }
      // A click with any other kind is the default size, centred where it landed
      // (QA 2026-09-24, shapes-F9).
      if (still && ctx.node(drawing.id)) {
        const r = kind === 'rect' ? { radii: cornersFor(CLICK_SIZE, CLICK_SIZE) } : {};
        const x = drawing.origin.x - CLICK_SIZE / 2;
        const y = drawing.origin.y - CLICK_SIZE / 2;
        ctx.edit([{ op: 'set', id: drawing.id, props: { width: CLICK_SIZE, height: CLICK_SIZE, ...r, transform: [1, 0, 0, 1, x, y] } }]);
      }
      drawing = null;
      ctx.stopCapturing();
      // A shape drawn is a shape to be worked on: the tool hands back to select.
      ctx.finish();
    },

    /**
     * The shape's own undo step stays open from the press until it is finished or given up,
     * so that giving it up (Escape, or Cmd+Z while placing) takes the whole shape back as
     * one thing, rather than leaving its creation behind as a step of its own.
     */
    holdsStep: () => drawing !== null,

    /**
     * Escape gives up the shape in hand: a line placed but never finished, or a shape
     * half-way through its drag, which used to be made anyway on release
     * (QA 2026-09-24, shapes-F15) — the brush gives up the same way. While a line is being
     * placed, Cmd+Z and Delete give it up too: they used to remove it underneath the tool,
     * and the next click then finished a line that was no longer there
     * (QA 2026-09-24, shapes-F14).
     */
    onKeyDown(event, ctx) {
      if (!drawing) return false;
      // Shift pressed with the hand still: the line straightens now, not on the next move.
      if (event.key === 'Shift') {
        if (kind === 'line' && lastLineAt) stretchLine(ctx, lastLineAt, true);
        return false;
      }
      const meta = event.metaKey || event.ctrlKey;
      const givesUp =
        event.key === 'Escape' || (placing && ((meta && event.key.toLowerCase() === 'z') || event.key === 'Delete' || event.key === 'Backspace'));
      if (!givesUp) return false;
      event.preventDefault();
      // A line being placed has no press in progress, so Select takes over as it always
      // has; mid-drag the hand is still down, and the tool stays in it, as the brush does.
      const wasPlacing = placing;
      cancel(ctx);
      if (wasPlacing) ctx.finish();
      return true;
    },

    onKeyUp(event, ctx) {
      if (event.key === 'Shift' && drawing && kind === 'line' && lastLineAt) stretchLine(ctx, lastLineAt, false);
    },

    deactivate(ctx) {
      // A half-drawn line does not survive putting the tool down: it would be a line of
      // whatever length the pointer last happened to be at, which nobody asked for.
      if (placing && drawing && ctx.node(drawing.id)) ctx.edit([{ op: 'delete', id: drawing.id }]);
      placing = false;
      drawing = null;
    },

    /**
     * The five kinds above, and whatever the kind in hand is described by below.
     *
     * All five are offered whichever one is in hand, because to the person drawing they
     * are one tool with five settings — which is exactly what the rail is for. They used
     * to hide in a menu over the bar, where you had to know they were there.
     *
     * A kind describable by one number gets one well; the star gets two, because how many
     * points it has and how deep they cut are different questions and answering only one
     * of them draws a shape nobody asked for. A kind with nothing to say leaves the wells
     * out, so the rail shortens rather than showing a hole.
     */
    rail(ctx: ToolContext): Rail {
      const kinds: RailButton[] = KINDS.map((item) => ({
        id: item.id,
        icon: item.icon,
        title: item.title,
        pressed: kind === item.id,
        run: () => ctx.chooseTool(item.id),
      }));
      const change = (patch: Partial<typeof settings>) => {
        Object.assign(settings, patch);
        ctx.railChanged();
      };

      if (kind === 'rect') {
        return {
          top: { kind: 'buttons', items: kinds },
          style: true,
          middle: {
            kind: 'slider',
            icon: 'corner',
            title: 'Corner radius',
            value: settings.radius,
            min: 0,
            max: 200,
            onChange: (radius) => change({ radius }),
          },
        };
      }
      if (kind === 'polygon') {
        return {
          top: { kind: 'buttons', items: kinds },
          style: true,
          middle: {
            kind: 'slider',
            icon: 'polygon',
            title: 'Sides',
            value: settings.sides,
            min: 3,
            max: 24,
            step: 1,
            onChange: (sides) => change({ sides }),
          },
        };
      }
      if (kind === 'star') {
        return {
          top: { kind: 'buttons', items: kinds },
          middle: {
            kind: 'slider',
            icon: 'star',
            title: 'Points',
            value: settings.points,
            min: 3,
            max: 24,
            step: 1,
            onChange: (points) => change({ points }),
          },
          style: true,
          bottom: {
            kind: 'slider',
            icon: 'corner',
            title: 'How deep the points cut',
            value: Math.round(settings.innerRatio * 100),
            min: 5,
            max: 95,
            format: (value) => `${value}%`,
            onChange: (value) => change({ innerRatio: value / 100 }),
          },
        };
      }
      // An ellipse and a line are described by the box they are dragged into and nothing
      // else, so they show the kinds and the style and stop there.
      return { top: { kind: 'buttons', items: kinds }, style: true };
    },
  };
}
